"use client";

import { useMemo, type CSSProperties } from "react";

import type { StoryChapter } from "@/lib/stories";
import { cn } from "@/lib/utils";

import { formatTime } from "./format-time";
import type { VideoTransport } from "./use-video-transport";

interface VideoTransportControlsProps {
  transport: VideoTransport;
  chapters: StoryChapter[];
  /** Runtime as authored, e.g. "2:41". Falls back to the clip's metadata. */
  runtime?: string;
  onToggleFullscreen: () => void;
  /**
   * The chrome has retracted off a playing clip. Nothing under it should be
   * clickable then, including the controls themselves, which are still in the
   * DOM and still keyboard-reachable (tabbing to one brings the bar back).
   */
  retracted?: boolean;
  className?: string;
}

const BACK_10_PATH = "M10 4.5a5.5 5.5 0 1 1-5.1 3.6M7.6 2.4 4.7 5.1l3.1 2.4";

function Icon({ path, size = 18 }: { path: string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      aria-hidden
      fill="none"
      className="shrink-0"
    >
      <path
        d={path}
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The drawn controls shared by the index player and the story page: chapter
 * label, seekable scrubber with chapter marks, transport, volume and fullscreen.
 * One component so the two players cannot theme apart, which they did as soon as
 * the story page grew its own bar.
 */
export default function VideoTransportControls({
  transport,
  chapters,
  runtime,
  onToggleFullscreen,
  retracted = false,
  className,
}: VideoTransportControlsProps) {
  const {
    playing,
    elapsed,
    duration,
    volume,
    muted,
    togglePlay,
    seekBy,
    scrub,
    changeVolume,
    toggleMute,
  } = transport;

  const currentChapter = useMemo(() => {
    if (chapters.length === 0) {
      return null;
    }
    let found = chapters[0]!;
    let index = 0;
    chapters.forEach((chapter, i) => {
      if (elapsed >= chapter.time) {
        found = chapter;
        index = i;
      }
    });
    return { chapter: found, number: index + 1 };
  }, [chapters, elapsed]);

  const progress = duration > 0 ? (elapsed / duration) * 100 : 0;
  const level = muted ? 0 : volume;

  // The bar is a band across the bottom of the frame, and on a phone that band
  // reaches the middle of it: left hit-testable, it swallows both the centred
  // play button and the tap on the film that pauses it. Only the clusters that
  // hold real controls take the pointer.
  const hit = retracted ? undefined : "pointer-events-auto";

  return (
    <div
      className={cn(
        // Insets grow with the frame: at 840px and up a flat 32px/20px left the
        // bar looking stuck to the bottom edge.
        "pointer-events-none absolute inset-x-0 bottom-0 px-6 pb-5 sm:px-8 sm:pb-6 lg:px-10 lg:pb-8",
        className,
      )}
    >
      {/* pointer-events-none: the controls paint over the centred play button,
          and on a phone the frame is short enough that this label lands right on
          it. A caption has no business eating that tap. */}
      {currentChapter && (
        <p className="pointer-events-none mb-4 flex items-center gap-2.5 font-mono text-[0.6875rem] tracking-[0.1em] text-white/75 uppercase">
          <span className="bg-marketing-blue size-1.5 rounded-full" />
          Chapter {currentChapter.number}
          {/* The title is dropped on small screens: the frame is ~190px tall
              there and the line would run under the centred play button. */}
          <span className="hidden sm:inline">
            · {currentChapter.chapter.label}
          </span>
        </p>
      )}

      <div className={cn("relative", hit)}>
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={elapsed}
          onChange={(event) => {
            scrub(Number(event.target.value));
          }}
          aria-label="Seek"
          aria-valuetext={`${formatTime(elapsed)} of ${formatTime(duration)}`}
          style={{ "--progress": `${progress}%` } as CSSProperties}
          className="h-1 w-full cursor-pointer appearance-none rounded-full bg-[linear-gradient(to_right,var(--color-marketing-blue)_0_var(--progress),rgba(255,255,255,0.28)_var(--progress)_100%)] [&::-webkit-slider-thumb]:size-3.5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[var(--color-marketing-blue)]"
        />
        {duration > 0 &&
          chapters.map((chapter) => (
            <span
              key={chapter.time}
              aria-hidden
              style={{ left: `${(chapter.time / duration) * 100}%` }}
              className="pointer-events-none absolute top-0 h-1 w-0.5 -translate-x-1/2 bg-black/55"
            />
          ))}
      </div>

      <div className="mt-3 flex items-center justify-between gap-4">
        <div className={cn("flex items-center gap-4 text-white sm:gap-5", hit)}>
          <button
            type="button"
            onClick={togglePlay}
            aria-label={playing ? "Pause" : "Play"}
            className="transition-opacity hover:opacity-80"
          >
            {playing ? (
              <svg width="18" height="20" viewBox="0 0 18 20" aria-hidden>
                <rect
                  x="2.5"
                  y="1.5"
                  width="4.5"
                  height="17"
                  rx="1.5"
                  fill="currentColor"
                />
                <rect
                  x="11"
                  y="1.5"
                  width="4.5"
                  height="17"
                  rx="1.5"
                  fill="currentColor"
                />
              </svg>
            ) : (
              <svg width="18" height="20" viewBox="0 0 18 20" aria-hidden>
                <path d="M3 2 L16 10 L3 18 Z" fill="currentColor" />
              </svg>
            )}
          </button>

          <button
            type="button"
            onClick={() => {
              seekBy(-10);
            }}
            aria-label="Back 10 seconds"
            className="transition-opacity hover:opacity-80"
          >
            <Icon path={BACK_10_PATH} size={19} />
          </button>

          {/* Volume: the button carries the state on every viewport, the rocker
              appears where there is room for it. Phones have hardware keys and
              no hover, so a 16px track there would be a decoration. */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleMute}
              aria-label={muted ? "Unmute" : "Mute"}
              className="transition-opacity hover:opacity-80"
            >
              {muted || level === 0 ? (
                <Icon path="M10.5 4.5 6.5 7.5H3.5v5h3l4 3v-11ZM14 8l4 4m0-4-4 4" />
              ) : (
                <Icon
                  path={`M10.5 4.5 6.5 7.5H3.5v5h3l4 3v-11ZM13.5 7.5a3.5 3.5 0 0 1 0 5${
                    level > 0.5 ? "M15.8 5.2a7 7 0 0 1 0 9.6" : ""
                  }`}
                />
              )}
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={level}
              onChange={(event) => {
                changeVolume(Number(event.target.value));
              }}
              aria-label="Volume"
              aria-valuetext={`${Math.round(level * 100)}%`}
              style={{ "--level": `${level * 100}%` } as CSSProperties}
              className="hidden h-1 w-16 cursor-pointer appearance-none rounded-full bg-[linear-gradient(to_right,#fff_0_var(--level),rgba(255,255,255,0.28)_var(--level)_100%)] sm:block [&::-webkit-slider-thumb]:size-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white"
            />
          </div>

          <span className="font-mono text-[0.6875rem] tracking-[0.04em] text-white/70">
            {formatTime(elapsed)} / {runtime ?? formatTime(duration)}
          </span>
        </div>

        <div className={cn("flex items-center gap-5 text-white/75", hit)}>
          {chapters.length > 0 && (
            <span className="hidden font-mono text-[0.6875rem] tracking-[0.09em] uppercase sm:inline">
              {chapters.length} chapters
            </span>
          )}
          <button
            type="button"
            onClick={onToggleFullscreen}
            aria-label="Toggle fullscreen"
            className="transition-colors hover:text-white"
          >
            <Icon path="M2 6V2h4M16 6V2h-4M2 12v4h4M16 12v4h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
